import { useEffect, useMemo, useState } from "react";
import { optimizedImage } from "@/lib/cloudinaryImage";
import { brandOpenState } from "@/lib/brandAvailability";
import { formatMoney } from "@/lib/orderUtils";

const SLIDE_MS = 3500;
const DEAL_MS = 6000;

const buildSlides = (brands) => {
    const logos = brands.filter((b) => b.image_url).map((b) => ({ type: "logo", key: `l-${b.id}`, brand: b }));
    const deals = brands.filter((b) => brandOpenState(b).available).flatMap((b) =>
        (b.items || []).filter((it) => it.kiosk_deal && it.available !== false)
            .map((it) => ({ type: "deal", key: `d-${it.id}`, brand: b, item: it })));
    if (!deals.length) return logos;
    const out = [];
    const gap = Math.max(1, Math.floor(logos.length / deals.length));
    let di = 0;
    logos.forEach((l, n) => { out.push(l); if (di < deals.length && (n + 1) % gap === 0) out.push(deals[di++]); });
    while (di < deals.length) out.push(deals[di++]);
    return out;
};

const DealSlide = ({ brand, item }) => (
    <div className="absolute inset-0 flex items-center gap-10 text-left">
        {item.image_url && (
            <img src={optimizedImage(item.image_url, { w: 900 })} alt={item.name} className="w-1/2 h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        <div className="flex-1 min-w-0">
            <p className="font-mono text-[15px] uppercase tracking-[0.2em] font-bold" style={{ color: brand.accent }}>{brand.name} · Today's deal</p>
            <h2 className="font-display text-[clamp(36px,5vw,64px)] leading-[0.95] mt-3">{item.name}</h2>
            {item.description && <p className="text-muted text-[17px] mt-4 line-clamp-3">{item.description}</p>}
            <p className="font-display text-gold text-[clamp(32px,4vw,52px)] mt-5" data-testid="kiosk-deal-price">{formatMoney(item.price)}</p>
        </div>
    </div>
);

export const KioskAttract = ({ brands, onStart }) => {
    const slides = useMemo(() => buildSlides(brands), [brands]);
    const [i, setI] = useState(0);
    const cur = slides[i % (slides.length || 1)];

    useEffect(() => {
        if (slides.length < 2) return;
        const t = setTimeout(() => setI((n) => (n + 1) % slides.length), cur?.type === "deal" ? DEAL_MS : SLIDE_MS);
        return () => clearTimeout(t);
    }, [i, slides.length, cur?.type]);

    const isDeal = cur?.type === "deal";
    return (
        <button onClick={() => onStart(isDeal ? cur.brand : null)} data-testid="kiosk-attract"
            className="fixed inset-0 bg-[var(--bg)] text-[var(--text)] cursor-pointer overflow-hidden">
            <div className="absolute inset-0 hero-grid opacity-50" aria-hidden />
            <div className="absolute inset-0 hero-glow" aria-hidden />
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-8">
                <p className="eyebrow">// Welcome to Naami Kitchens</p>
                <div className={`relative my-10 transition-[width,height] duration-700 ${isDeal ? "w-[min(88vw,1100px)] h-[min(48vh,440px)]" : "w-[min(70vw,560px)] h-[min(38vh,340px)]"}`} data-testid="kiosk-attract-logos">
                    {slides.map((s, n) => (
                        <div key={s.key} className={`attract-logo ${n === i ? "is-on" : ""}`}
                            data-testid={n === i ? (s.type === "deal" ? "kiosk-attract-deal-active" : "kiosk-attract-logo-active") : undefined}>
                            {s.type === "deal" ? <DealSlide brand={s.brand} item={s.item} />
                                : <img src={optimizedImage(s.brand.image_url, { w: 800 })} alt={s.brand.name} className="w-full h-full object-contain" />}
                        </div>
                    ))}
                </div>
                <h1 className="font-display leading-[0.92]" style={{ fontSize: "clamp(56px, 10vw, 128px)" }}>
                    Tap to <span className="text-gold">order.</span>
                </h1>
                <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-gold animate-pulse">Touch anywhere</p>
            </div>
            <div className="absolute bottom-6 left-0 right-0 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-muted">
                1 Saltire Sq · Edinburgh · Open 11:00 – 23:00
            </div>
        </button>
    );
};
